import { useState } from 'react';

import { useGetFormalOffer, useSplitTests } from '@api/lendingAPI/lapiHooks';
import { FormattedMessage } from 'react-intl';

import { ConfirmedModal } from './ConfirmedModal';
import { CrossSellOfferTerms } from './creditCardCrossSell.types';
import { DeclineModal } from './DeclineModal';
import { GeneratingOfferLoading } from './GeneratingOfferLoading';
import { getOfferModalProps } from './getOfferModalProps';
import { InterstitialOffer } from './InterstitialOffer';
import { OfferModal } from './OfferModal';
import { RejectionModal } from './RejectionModal';
import { useCreditCardWaitlist } from './useCreditCardWaitlist';
import { useGenerateCreditCardOffer } from './useGenerateCreditCardOffer';
import { useMarkContinue } from './useMarkContinue';

const CONTINUE_SIGNAL_TIMEOUT_MS = 1500;

type CreditCardWaitlistInterstitialProps = {
    offerId: string;
    amount: number;
    goToHome: () => void;
    onLeaveOffer?: () => void;
    onEnterOfferFlow?: () => void;
};

type ViewState =
    | 'offer'
    | 'confirmed'
    | 'rejection'
    | 'generating'
    | 'offerResult'
    | 'declined';

export const CreditCardWaitlistInterstitial = ({
    offerId,
    amount,
    goToHome,
    onLeaveOffer,
    onEnterOfferFlow
}: CreditCardWaitlistInterstitialProps) => {
    const [view, setView] = useState<ViewState>('offer');
    const [offerTerms, setOfferTerms] = useState<CrossSellOfferTerms | null>(
        null
    );
    const { formalOffer } = useGetFormalOffer(offerId);
    const { mutate } = useCreditCardWaitlist();
    const { mutate: generateOffer } = useGenerateCreditCardOffer();
    const { mutateAsync: markContinue } = useMarkContinue();

    const isCrossSellOfferEnabled = useSplitTests(
        'cc_cross_sell_offer_enabled'
    );

    const formalOfferId = formalOffer?.formal_offer_id;

    if (!formalOfferId) {
        return null;
    }

    const handleAccept = () => {
        onLeaveOffer?.();

        if (isCrossSellOfferEnabled) {
            onEnterOfferFlow?.();
            setView('generating');
            generateOffer(undefined, {
                onSuccess: (response) => {
                    if (response.status === 'offer' && response.offer) {
                        setOfferTerms(response.offer);
                        setView('offerResult');
                    } else {
                        setView('declined');
                    }
                },
                onError: () => setView('declined')
            });
            return;
        }

        mutate({
            response: 'accepted',
            offer_id: formalOfferId,
            drawdown_amount: String(amount)
        });
        setView('confirmed');
    };

    const handleReject = () => {
        setView('rejection');
        onLeaveOffer?.();
    };

    const handleDismiss = () => {
        mutate({
            response: 'dismissed',
            offer_id: formalOfferId,
            drawdown_amount: String(amount)
        });
        goToHome();
    };

    const handleContinueToActivation = async () => {
        await Promise.race([
            markContinue().catch(() => {}),
            new Promise((resolve) =>
                setTimeout(resolve, CONTINUE_SIGNAL_TIMEOUT_MS)
            )
        ]);
        window.location.href = '/credit-card/dashboard';
    };

    const handleBack = () => {
        setView('offer');
    };

    const handleSubmitRejection = (reason?: string, detail?: string) => {
        mutate({
            response: 'rejected',
            offer_id: formalOfferId,
            drawdown_amount: String(amount),
            rejection_reason: reason as
                | 'already_have_business_card'
                | 'dont_need_credit_card'
                | 'benefits_not_compelling'
                | 'prefer_flexi_loan'
                | 'other'
                | undefined,
            rejection_reason_detail: detail
        });
        goToHome();
    };

    const handleSkipRejection = () => {
        mutate({
            response: 'rejected',
            offer_id: formalOfferId,
            drawdown_amount: String(amount)
        });
        goToHome();
    };

    return (
        <div className="bg-white">
            {view === 'offer' && (
                <>
                    <InterstitialOffer
                        onAccept={handleAccept}
                        onReject={handleReject}
                        variant={isCrossSellOfferEnabled ? 'offer' : 'waitlist'}
                    />
                    <button
                        onClick={handleDismiss}
                        className="hover:text-primary-30 mt-m block w-full text-center text-s text-primary-40 underline"
                    >
                        <FormattedMessage
                            id="_.credit_card_waitlist.dismiss_link"
                            defaultMessage="Continue to dashboard"
                        />
                    </button>
                </>
            )}
            {view === 'generating' && <GeneratingOfferLoading />}
            {view === 'offerResult' && offerTerms && (
                <OfferModal
                    {...getOfferModalProps(offerTerms)}
                    onMaybeLater={goToHome}
                    onContinue={handleContinueToActivation}
                />
            )}
            {view === 'declined' && <DeclineModal onGoToDashboard={goToHome} />}
            <ConfirmedModal
                isOpen={view === 'confirmed'}
                onGoToDashboard={goToHome}
            />
            <RejectionModal
                isOpen={view === 'rejection'}
                onBack={handleBack}
                onSubmit={handleSubmitRejection}
                onSkip={handleSkipRejection}
            />
        </div>
    );
};
